import type {
  BuildPreviewResponse,
  MindmapConcept,
  SectionPodcastSummary,
} from '@/types/api';

// Mock data for media components

export interface TranscriptItem {
  id: string;
  startTime: number;
  endTime?: number;
  timestamp: string;
  text: string;
}

// Lesson heading - same across all media types and styles
export const mockLessonHeading = {
  title: 'Introduction to Machine Learning',
  subtitle: 'Understanding the fundamentals of AI and machine learning',
};

// Mock mindmap markdown data
/**
 * A mindmap shaped like real output: `05-mindmap-from-bytes` asks for one `#`
 * root, one `##` per concept and 2-4 `###` sub-branches, headings only,
 * labels of 8 words or fewer. 19 nodes over 3 levels, which is the middle of
 * the 15-40 real lessons produce. The default for the mindmap handler, so
 * stories show what learners actually see; `mockMindmapMarkdown` below is the
 * 74-node stress case.
 */
export const mockMindmapPromptShaped = `# Photosynthesis
## Light reactions
### Thylakoid membrane
### Water splitting
### ATP and NADPH
## Calvin cycle
### Carbon fixation
### RuBisCO enzyme
### G3P to glucose
## Chloroplast
### Stroma
### Grana stacks
## Pigments
### Chlorophyll a and b
### Carotenoids
## Limiting factors
### Light intensity
### Carbon dioxide level
### Temperature`;

/**
 * A mind map as the lesson plan writes it (backend `lesson/plan.py`
 * `mindmap_markdown`): `#` lesson, `##` section, `###` concept, then up to
 * three of the concept's key points as bullets. Paired with
 * `mockMindmapConcepts`, whose names match the `###` nodes - what makes a
 * concept node open its panel.
 */
export const mockMindmapPlanShaped = `# Photosynthesis
## Light reactions
### Thylakoid membrane
- Stacked membranes inside the chloroplast
- Hold the chlorophyll that captures light
### Water splitting
- Light energy splits water molecules
- Releases oxygen as a by-product
## Calvin cycle
### Carbon fixation
- RuBisCO attaches CO2 to RuBP
- Runs on ATP and NADPH from the light reactions
### G3P to glucose
- Two G3P molecules make one glucose`;

/**
 * The plan's DRAFT outline, as `GET /lessons/{id}/mindmap` serves it while
 * `byte_selection` checks coverage (`stage: 'checking'`, `status:
 * 'processing'`, no `concepts`): sections, concepts and up to three key
 * points each, as `draft_mindmap_markdown` writes it. The build view draws the
 * clear levels and the next one as faint, wordless nodes.
 */
export const mockMindmapDraftOutline = `# Photosynthesis
## Light reactions
### Thylakoid membrane
- Stacks of membrane discs inside the chloroplast
- Holds the pigments and electron carriers
### Water splitting
- Photosystem II splits water to replace lost electrons
- Releases oxygen as a by-product
- Supplies protons for the gradient
### Making ATP and NADPH
- ATP synthase uses the proton gradient
- NADPH carries electrons to the Calvin cycle
## Calvin cycle
### Carbon fixation
- RuBisCO attaches CO2 to RuBP
- Runs on ATP and NADPH from the light reactions
### Reduction
- 3-PGA is reduced to G3P
### G3P to glucose
- Two G3P molecules make one glucose
- Most G3P regenerates RuBP`;

/**
 * `GET /lessons/{id}/build-preview` for a lesson with two read sources: what
 * the build view's "What's coming" panel shows while the outline is planned.
 * The textbook has more sections (11) than the endpoint sends (8 max), so the
 * panel shows "+3 more sections"; the notes have no summary.
 */
export const mockBuildPreview: BuildPreviewResponse = {
  sources: [
    {
      name: 'Biology Textbook Ch.4',
      summary:
        'How plants capture light and store its energy as sugar: the light reactions in the thylakoids, the Calvin cycle in the stroma, and what limits the rate of both.',
      section_count: 11,
      sections: [
        {
          title: 'Light and pigments',
          key_points: ['Chlorophyll absorbs red and blue light'],
        },
        {
          title: 'The thylakoid membrane',
          key_points: ['Photosystems sit in stacked membranes'],
        },
        {
          title: 'Splitting water',
          key_points: ['Water is the source of the oxygen released'],
        },
        {
          title: 'Making ATP and NADPH',
          key_points: ['An electron chain pumps protons to drive ATP synthase'],
        },
        {
          title: 'Carbon fixation',
          key_points: ['RuBisCO attaches CO2 to RuBP'],
        },
        {
          title: 'From G3P to glucose',
          key_points: ['Two G3P molecules make one glucose'],
        },
        {
          title: 'Limiting factors',
          key_points: ['Light, CO2 and temperature each cap the rate'],
        },
        {
          title: 'C4 and CAM plants',
          key_points: ['They concentrate CO2 to avoid photorespiration'],
        },
      ],
    },
    {
      name: 'Lecture notes, week 3',
      summary: '',
      section_count: 2,
      sections: [
        { title: 'Photosynthesis vs respiration', key_points: [] },
        {
          title: 'Worked exam question',
          key_points: ['Read the graph before naming the limiting factor'],
        },
      ],
    },
  ],
};

/**
 * The plan's concepts for `mockMindmapPlanShaped`. Carbon fixation shows all
 * three kinds of prerequisite: one taught in this lesson (Water splitting),
 * one from another lesson in Mindspace (Cellular respiration) and one the
 * student has not studied (Enzymes). Thylakoid membrane has not reached
 * Mindspace yet (`mindspace_id: null`).
 */
export const mockMindmapConcepts: MindmapConcept[] = [
  {
    concept_id: 'lc-thylakoid',
    mindspace_id: null,
    name: 'Thylakoid membrane',
    description:
      'The stacked membranes inside a chloroplast where the light reactions happen.',
    section: 'Light reactions',
    learning_goal: 'Point to where light is captured in a chloroplast',
    key_points: [
      'Stacked membranes inside the chloroplast',
      'Hold the chlorophyll that captures light',
    ],
    misconceptions: [],
    prerequisites: [],
  },
  {
    concept_id: 'lc-water',
    mindspace_id: 'ms-water',
    name: 'Water splitting',
    description:
      'Light energy breaks water into hydrogen ions, electrons and oxygen.',
    section: 'Light reactions',
    learning_goal: 'Explain where the oxygen from photosynthesis comes from',
    key_points: [
      'Light energy splits water molecules',
      'Releases oxygen as a by-product',
    ],
    misconceptions: ['Thinking the oxygen comes from carbon dioxide'],
    prerequisites: [],
  },
  {
    concept_id: 'lc-fixation',
    mindspace_id: 'ms-fixation',
    name: 'Carbon fixation',
    description:
      'The Calvin cycle step where RuBisCO attaches carbon dioxide to a five-carbon sugar.',
    section: 'Calvin cycle',
    learning_goal: 'Describe how carbon dioxide becomes part of a sugar',
    key_points: [
      'RuBisCO attaches CO2 to RuBP',
      'Runs on ATP and NADPH from the light reactions',
    ],
    misconceptions: [
      'Thinking the Calvin cycle needs darkness',
      'Confusing RuBisCO with RuBP',
    ],
    prerequisites: [
      {
        name: 'Water splitting',
        concept_id: 'lc-water',
        mindspace_id: 'ms-water',
      },
      {
        name: 'Cellular respiration',
        concept_id: null,
        mindspace_id: 'ms-respiration',
      },
      { name: 'Enzymes', concept_id: null, mindspace_id: null },
    ],
  },
  {
    concept_id: 'lc-g3p',
    mindspace_id: 'ms-g3p',
    name: 'G3P to glucose',
    description: 'How the three-carbon sugar G3P is built into glucose.',
    section: 'Calvin cycle',
    learning_goal: '',
    key_points: ['Two G3P molecules make one glucose'],
    misconceptions: [],
    prerequisites: [
      {
        name: 'Carbon fixation',
        concept_id: 'lc-fixation',
        mindspace_id: 'ms-fixation',
      },
    ],
  },
];

export const mockMindmapMarkdown = `# Introduction to Machine Learning

## Core Concepts

### Supervised Learning
- Uses labeled training data
- Learn from examples with known answers
- **Types of Problems**
  - Classification (categories)
    - Spam detection
    - Image recognition
  - Regression (numbers)
    - Price prediction
    - Temperature forecasting

### Unsupervised Learning
- Finds patterns in unlabeled data
- No correct answers provided
- **Common Techniques**
  - Clustering
    - Customer segmentation
    - Document grouping
  - Dimensionality reduction
    - Feature extraction
    - Data visualization

## Machine Learning Workflow

### Data Preparation
- Data collection
- Data cleaning
- Feature engineering
- Data splitting
  - Training set (60-70%)
  - Validation set (15-20%)
  - Test set (15-20%)

### Model Training
- Algorithm selection
- Hyperparameter tuning
- Training process
- Performance monitoring

### Evaluation
- Accuracy metrics
- Precision and recall
- Confusion matrix
- Cross-validation

## Key Challenges

### Overfitting
- Model too complex
- Memorizes training data
- Poor generalization
- **Solutions**
  - Regularization
  - More training data
  - Simpler models

### Underfitting
- Model too simple
- Fails to capture patterns
- Poor performance overall
- **Solutions**
  - More complex models
  - Better features
  - More training time

## Applications

### Business
- Recommendation systems
- Fraud detection
- Customer churn prediction
- Sales forecasting

### Healthcare
- Medical diagnosis
- Drug discovery
- Patient monitoring
- Treatment optimization

### Technology
- Autonomous vehicles
- Natural language processing
- Computer vision
- Speech recognition`;

// Mock transcript data for video - one line at a time
export const mockVideoTranscriptData: TranscriptItem[] = [
  {
    id: '1',
    startTime: 0,
    endTime: 5,
    timestamp: '00:00',
    text: 'Welcome to our machine learning fundamentals series.',
  },
  {
    id: '2',
    startTime: 5,
    endTime: 10,
    timestamp: '00:05',
    text: "Today we'll explore core concepts of modern machine learning.",
  },
  {
    id: '3',
    startTime: 10,
    endTime: 18,
    timestamp: '00:10',
    text: 'Machine learning teaches computers to find patterns in data.',
  },
  {
    id: '4',
    startTime: 18,
    endTime: 25,
    timestamp: '00:18',
    text: 'This happens without explicit programming for each task.',
  },
  {
    id: '5',
    startTime: 25,
    endTime: 32,
    timestamp: '00:25',
    text: "Let's start with supervised learning fundamentals.",
  },
  {
    id: '6',
    startTime: 32,
    endTime: 40,
    timestamp: '00:32',
    text: 'Supervised learning uses labeled training data.',
  },
  {
    id: '7',
    startTime: 40,
    endTime: 48,
    timestamp: '00:40',
    text: 'The algorithm learns from examples with known answers.',
  },
  {
    id: '8',
    startTime: 48,
    endTime: 55,
    timestamp: '00:48',
    text: 'Then it makes predictions on new, unseen data.',
  },
  {
    id: '9',
    startTime: 55,
    endTime: 62,
    timestamp: '00:55',
    text: 'Classification predicts categories like spam detection.',
  },
  {
    id: '10',
    startTime: 62,
    endTime: 70,
    timestamp: '01:02',
    text: 'Regression predicts continuous values like prices.',
  },
];

// Mock transcript data for audio - podcast style
export const mockAudioTranscriptData: TranscriptItem[] = [
  {
    id: '1',
    startTime: 0,
    endTime: 4,
    timestamp: '00:00',
    text: 'Welcome to the Machine Learning Fundamentals podcast.',
  },
  {
    id: '2',
    startTime: 4,
    endTime: 8,
    timestamp: '00:04',
    text: "I'm your host, and today we're diving deep into core concepts.",
  },
  {
    id: '3',
    startTime: 8,
    endTime: 13,
    timestamp: '00:08',
    text: 'Machine learning is transforming how computers solve problems.',
  },
  {
    id: '4',
    startTime: 13,
    endTime: 18,
    timestamp: '00:13',
    text: 'Instead of programming every solution, we teach computers to learn.',
  },
  {
    id: '5',
    startTime: 18,
    endTime: 23,
    timestamp: '00:18',
    text: 'This happens through patterns discovered in data.',
  },
  {
    id: '6',
    startTime: 23,
    endTime: 28,
    timestamp: '00:23',
    text: "Let's start with the most common type: supervised learning.",
  },
  {
    id: '7',
    startTime: 28,
    endTime: 33,
    timestamp: '00:28',
    text: 'Supervised learning uses examples with known correct answers.',
  },
  {
    id: '8',
    startTime: 33,
    endTime: 38,
    timestamp: '00:33',
    text: 'Think of it like teaching a child to recognize animals.',
  },
  {
    id: '9',
    startTime: 38,
    endTime: 43,
    timestamp: '00:38',
    text: 'You show them many photos labeled "cat" or "dog".',
  },
  {
    id: '10',
    startTime: 43,
    endTime: 48,
    timestamp: '00:43',
    text: "Eventually, they can identify new animals they've never seen.",
  },
  {
    id: '11',
    startTime: 48,
    endTime: 54,
    timestamp: '00:48',
    text: 'The beauty of machine learning is its ability to scale.',
  },
  {
    id: '12',
    startTime: 54,
    endTime: 60,
    timestamp: '00:54',
    text: 'Traditional programming requires writing rules for every scenario.',
  },
  {
    id: '13',
    startTime: 60,
    endTime: 66,
    timestamp: '01:00',
    text: 'But machine learning algorithms discover these rules automatically.',
  },
  {
    id: '14',
    startTime: 66,
    endTime: 72,
    timestamp: '01:06',
    text: "Let's explore the two main categories of supervised learning problems.",
  },
  {
    id: '15',
    startTime: 72,
    endTime: 78,
    timestamp: '01:12',
    text: 'First, we have classification tasks.',
  },
  {
    id: '16',
    startTime: 78,
    endTime: 84,
    timestamp: '01:18',
    text: 'Classification is about predicting which category something belongs to.',
  },
  {
    id: '17',
    startTime: 84,
    endTime: 90,
    timestamp: '01:24',
    text: 'Email spam detection is a perfect example of binary classification.',
  },
  {
    id: '18',
    startTime: 90,
    endTime: 96,
    timestamp: '01:30',
    text: 'Each email is either spam or not spam - two distinct categories.',
  },
  {
    id: '19',
    startTime: 96,
    endTime: 102,
    timestamp: '01:36',
    text: 'Image recognition is another powerful application of classification.',
  },
  {
    id: '20',
    startTime: 102,
    endTime: 108,
    timestamp: '01:42',
    text: 'Modern systems can classify images into thousands of different categories.',
  },
  {
    id: '21',
    startTime: 108,
    endTime: 114,
    timestamp: '01:48',
    text: 'From identifying dog breeds to detecting medical conditions in X-rays.',
  },
  {
    id: '22',
    startTime: 114,
    endTime: 120,
    timestamp: '01:54',
    text: 'The second major category is regression.',
  },
  {
    id: '23',
    startTime: 120,
    endTime: 126,
    timestamp: '02:00',
    text: 'Regression predicts continuous numerical values rather than categories.',
  },
  {
    id: '24',
    startTime: 126,
    endTime: 132,
    timestamp: '02:06',
    text: 'House price prediction is a classic regression problem.',
  },
  {
    id: '25',
    startTime: 132,
    endTime: 138,
    timestamp: '02:12',
    text: 'Given features like location, size, and age, predict the price.',
  },
  {
    id: '26',
    startTime: 138,
    endTime: 144,
    timestamp: '02:18',
    text: 'Weather forecasting also relies heavily on regression models.',
  },
  {
    id: '27',
    startTime: 144,
    endTime: 150,
    timestamp: '02:24',
    text: "Predicting tomorrow's temperature based on historical patterns and current conditions.",
  },
  {
    id: '28',
    startTime: 150,
    endTime: 156,
    timestamp: '02:30',
    text: "Now let's talk about the machine learning workflow.",
  },
  {
    id: '29',
    startTime: 156,
    endTime: 162,
    timestamp: '02:36',
    text: 'It all starts with data collection and preparation.',
  },
  {
    id: '30',
    startTime: 162,
    endTime: 168,
    timestamp: '02:42',
    text: 'Quality data is absolutely crucial for building effective models.',
  },
  {
    id: '31',
    startTime: 168,
    endTime: 174,
    timestamp: '02:48',
    text: 'Garbage in, garbage out - this principle is especially true in machine learning.',
  },
  {
    id: '32',
    startTime: 174,
    endTime: 180,
    timestamp: '02:54',
    text: 'Once we have clean data, we split it into different sets.',
  },
  {
    id: '33',
    startTime: 180,
    endTime: 186,
    timestamp: '03:00',
    text: 'The training set is used to teach our model the patterns.',
  },
  {
    id: '34',
    startTime: 186,
    endTime: 192,
    timestamp: '03:06',
    text: 'Typically, we allocate 60 to 70 percent of our data for training.',
  },
  {
    id: '35',
    startTime: 192,
    endTime: 198,
    timestamp: '03:12',
    text: 'The validation set helps us tune our model and select the best parameters.',
  },
  {
    id: '36',
    startTime: 198,
    endTime: 204,
    timestamp: '03:18',
    text: 'This usually gets about 15 to 20 percent of the data.',
  },
  {
    id: '37',
    startTime: 204,
    endTime: 210,
    timestamp: '03:24',
    text: 'Finally, the test set gives us an honest evaluation of model performance.',
  },
  {
    id: '38',
    startTime: 210,
    endTime: 216,
    timestamp: '03:30',
    text: 'This is data the model has never seen during training or validation.',
  },
  {
    id: '39',
    startTime: 216,
    endTime: 222,
    timestamp: '03:36',
    text: 'One of the biggest challenges in machine learning is avoiding overfitting.',
  },
  {
    id: '40',
    startTime: 222,
    endTime: 228,
    timestamp: '03:42',
    text: 'Overfitting happens when a model learns the training data too well.',
  },
  {
    id: '41',
    startTime: 228,
    endTime: 234,
    timestamp: '03:48',
    text: 'It memorizes specific examples rather than learning general patterns.',
  },
  {
    id: '42',
    startTime: 234,
    endTime: 240,
    timestamp: '03:54',
    text: 'Think of a student who memorizes answers without understanding concepts.',
  },
  {
    id: '43',
    startTime: 240,
    endTime: 246,
    timestamp: '04:00',
    text: 'They might ace practice tests but fail when questions are slightly different.',
  },
  {
    id: '44',
    startTime: 246,
    endTime: 252,
    timestamp: '04:06',
    text: 'On the flip side, we have underfitting.',
  },
  {
    id: '45',
    startTime: 252,
    endTime: 258,
    timestamp: '04:12',
    text: 'This occurs when our model is too simple to capture the underlying patterns.',
  },
  {
    id: '46',
    startTime: 258,
    endTime: 264,
    timestamp: '04:18',
    text: "It's like trying to explain quantum physics with elementary school math.",
  },
  {
    id: '47',
    startTime: 264,
    endTime: 270,
    timestamp: '04:24',
    text: 'The goal is finding the sweet spot between these two extremes.',
  },
  {
    id: '48',
    startTime: 270,
    endTime: 276,
    timestamp: '04:30',
    text: 'We want models that generalize well to new, unseen data.',
  },
  {
    id: '49',
    startTime: 276,
    endTime: 282,
    timestamp: '04:36',
    text: "That's all for today's episode on machine learning fundamentals.",
  },
  {
    id: '50',
    startTime: 282,
    endTime: 288,
    timestamp: '04:42',
    text: 'Join us next time as we explore deep learning and neural networks.',
  },
];

// Mock lesson content for different styles
export const mockLessonContent = {
  standard: `Machine learning is a subset of artificial intelligence (AI) that focuses on the development of algorithms and statistical models that enable computer systems to improve their performance on a specific task through experience, without being explicitly programmed.

## Core Concepts

### Supervised Learning
Supervised learning algorithms learn from labeled training data to make predictions or decisions. The algorithm analyzes training data and produces an inferred function, which can be used for mapping new examples.

**Key characteristics:**
- Uses labeled datasets for training
- Goal is to predict outcomes for new data
- Performance can be measured against known correct answers

### Types of Problems
1. **Classification**: Predicting categorical outcomes (e.g., spam vs. not spam)
2. **Regression**: Predicting continuous numerical values (e.g., house prices)

### Model Evaluation
Proper evaluation is crucial for understanding model performance:
- **Training Set**: Used to train the model
- **Validation Set**: Used for model selection and hyperparameter tuning
- **Test Set**: Used for final performance evaluation

The goal is to achieve good generalization - performing well on unseen data, not just the training data.`,

  eli5: `# Machine Learning - Simple Explanation

Think of machine learning like teaching a child to recognize animals!

## What is Machine Learning?
Imagine you're showing a child hundreds of pictures of cats and dogs. After seeing many examples, the child learns to tell the difference between cats and dogs, even when seeing new pictures they've never seen before.

Machine learning works the same way! We show computers lots of examples, and they learn patterns to make predictions about new things.

## How Does It Work?

### Learning from Examples (Supervised Learning)
Just like how you learned to read by looking at many books, computers learn by looking at lots of data with the right answers already known.

**For example:**
- Show the computer 1000 emails marked as "spam" or "not spam"
- The computer finds patterns (like certain words that appear in spam)
- Now it can guess if new emails are spam or not!

### Two Main Types of Problems
1. **Sorting into groups** (like separating cats from dogs in photos)
2. **Predicting numbers** (like guessing how much a house costs)

### Checking if the Computer Learned Well
We test the computer with new examples it has never seen before, just like giving a student a quiz with new questions!`,

  story: `# The Tale of the Learning Machine

Once upon a time, in a bustling tech company, there lived a curious computer named Claude who wanted to learn about the world just like humans do.

## Claude's First Lesson

Claude watched as thousands of emails flowed through the company's servers each day. Some were important messages from clients, while others were annoying spam trying to sell strange products. The human employees spent hours sorting through them.

"I want to help!" Claude thought. So the kind engineers decided to teach Claude by showing him examples.

### The Training Begins

Day after day, Claude studied emails that humans had already labeled:
- "This email about winning a lottery is SPAM"
- "This email from a client about a project is IMPORTANT"
- "This email trying to sell miracle pills is SPAM"

Claude started noticing patterns. Spam emails often used words like "URGENT," "FREE," and "WINNER," while important emails used professional language and mentioned real projects.

### The Big Test

After weeks of learning, it was time for Claude's final exam. The engineers gave him brand new emails he had never seen before. Claude confidently sorted them:
- An email with "You've won $1,000,000!" → SPAM
- An email asking about next week's meeting → IMPORTANT

Claude had learned to generalize from his training and could now help the company automatically sort emails!

### The Moral of the Story

Just like Claude learned to recognize spam, machine learning helps computers find patterns in data and make smart predictions about new information they've never seen before.`,

  revision: `# Machine Learning - Key Points Summary

## Definition
• Machine learning = computers learning patterns from data to make predictions
• Subset of artificial intelligence (AI)
• No explicit programming for each task

## Main Types

### Supervised Learning
• Uses labeled training data
• Learn from examples with known correct answers
• Two types:
  - Classification (categories): spam detection, image recognition
  - Regression (numbers): price prediction, temperature forecasting

### Key Process
1. **Training**: Show algorithm many examples with answers
2. **Learning**: Algorithm finds patterns in the data
3. **Testing**: Evaluate performance on new, unseen data
4. **Prediction**: Apply learned patterns to make decisions

## Essential Concepts

### Data Splitting
• Training set (60-70%): Teach the model
• Validation set (15-20%): Tune and select model
• Test set (15-20%): Final performance check

### Evaluation Metrics
• Accuracy: How often predictions are correct
• Precision: Of positive predictions, how many were right
• Recall: Of actual positives, how many were found

### Overfitting vs Underfitting
• Overfitting: Too specific to training data, poor on new data
• Underfitting: Too simple, poor performance overall
• Goal: Right balance for good generalization

## Real-World Applications
• Email spam filtering
• Recommendation systems (Netflix, Amazon)
• Medical diagnosis
• Autonomous vehicles
• Financial fraud detection`,
};

/**
 * Highlights carousels — the Inshorts-style card decks the Highlights sub-tab
 * renders, one carousel per concept.
 *
 * Shaped deliberately rather than filled with lorem, because three separate
 * behaviours read off this structure and each needs a distinguishing feature:
 *
 *  - **Vertical navigation** wraps between concepts, so there are three of
 *    them and each `concept_name` is visibly different.
 *  - **Horizontal navigation** wraps within a concept, and the pagination dots
 *    count `cards` — so the three carousels have 4, 3 and 2 cards. A fixture
 *    with a uniform card count cannot tell "dots follow the current concept"
 *    from "dots are hard-coded".
 *  - **Template choice** is `hash(concept_id) % 7` (see
 *    `highlights-template/picker.ts`). These three ids hash to 6, 2 and 3 - three
 *    different templates - so a story rendering all three also covers the
 *    picker instead of showing one layout three times. If you rename a
 *    `concept_id`, re-check that: the ids are load-bearing, not decorative.
 *
 * The topic matches `mockLessonContent` so a story mounting the whole lesson
 * page does not show highlights about a different subject than the text tab.
 */
export const mockHighlightCarousels = [
  {
    concept_id: 'concept-supervised-learning',
    concept_name: 'Supervised learning',
    cards: [
      {
        title: 'Learning from labelled examples',
        body: 'A supervised model is shown inputs paired with the right answer, and adjusts itself until its predictions match.',
      },
      {
        title: 'Features and labels',
        body: 'The features are what the model sees; the label is what it is asked to predict. Choosing features well matters more than model choice.',
      },
      {
        title: 'Classification vs regression',
        body: 'Predicting a category is classification. Predicting a number is regression. The difference decides the loss function.',
      },
      {
        title: 'Why held-out data matters',
        body: 'A model scored on data it trained on will look far better than it is. Always keep a test set it has never seen.',
      },
    ],
  },
  {
    concept_id: 'concept-gradient-descent',
    concept_name: 'Gradient descent',
    cards: [
      {
        title: 'Walking downhill',
        body: 'Gradient descent reads the slope of the error surface and steps in the direction that reduces error fastest.',
      },
      {
        title: 'The learning rate',
        body: 'Too small and training crawls. Too large and it overshoots the minimum and diverges. It is the first thing to tune.',
      },
      {
        title: 'Local minima',
        body: 'The surface is rarely a single bowl. Where you start can decide which minimum you end up in.',
      },
    ],
  },
  {
    concept_id: 'concept-clustering',
    concept_name: 'Clustering',
    cards: [
      {
        title: 'Grouping without labels',
        body: 'Clustering looks for structure in data nobody has annotated — points that sit near each other are assumed to belong together.',
      },
      {
        title: 'Choosing k',
        body: 'k-means partitions points around k centres. You pick k, which means you are already asserting something about the data.',
      },
    ],
  },
];

/**
 * A real, tiny, locally generated WAV as a `data:` URI.
 *
 * Exists so audio stories stop depending on the public internet.
 * `UI/AudioPlayer` used to point at a SoundHelix MP3 over HTTPS, which made a
 * test-suite story depend on a third party being up — and offline it silently
 * rendered the error state instead of a player, with no assertion to notice.
 *
 * One second of a 440Hz tone at 8kHz mono, 8-bit: ~8KB of samples, and the
 * whole thing is generated from arithmetic rather than committed as a binary
 * blob. Audible content rather than silence on purpose — a silent file decodes
 * to a flat waveform, which is exactly what a BROKEN decode also looks like.
 */
export function toneWavDataUri(
  seconds = 1,
  hz = 440,
  sampleRate = 8000
): string {
  const samples = seconds * sampleRate;
  const dataSize = samples;
  const buffer = new Uint8Array(44 + dataSize);
  const view = new DataView(buffer.buffer);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i += 1)
      view.setUint8(offset + i, text.charCodeAt(i));
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  ascii(8, 'WAVEfmt ');
  view.setUint32(16, 16, true); // PCM header size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate, true); // byte rate (8-bit mono)
  view.setUint16(32, 1, true); // block align
  view.setUint16(34, 8, true); // bits per sample
  ascii(36, 'data');
  view.setUint32(40, dataSize, true);
  for (let i = 0; i < samples; i += 1) {
    // 8-bit PCM is UNSIGNED: 128 is silence.
    view.setUint8(
      44 + i,
      128 + Math.round(96 * Math.sin((2 * Math.PI * hz * i) / sampleRate))
    );
  }
  let binary = '';
  for (const byte of buffer) binary += String.fromCharCode(byte);
  return `data:audio/wav;base64,${btoa(binary)}`;
}

export const mockPodcastAudioUrl = toneWavDataUri();

/**
 * Precomputed waveform peaks, the way the backend sends them.
 *
 * This is the seam that makes the podcast player storyable at all: given
 * `peaks` AND `duration`, wavesurfer renders from them and skips downloading
 * and decoding the audio entirely (see `AudioPlayer`'s prop docs). Without
 * them a story waits on `decodeAudioData`, which is the part that has no
 * deterministic moment to assert against.
 *
 * Generated rather than hand-typed, with the marketing site's waveform formula
 * (`xonera-landing-page/.../PodcastPanel.tsx`): a swell that rises and falls
 * across the clip, times deterministic per-bar detail, floored at 0.14. It
 * reads as speech rather than the regular sine pulse this used to be, and the
 * story matches the page that advertises it. Deterministic, so the canvas
 * never reshuffles between renders.
 */
export const mockPodcastPeaks: number[] = Array.from(
  { length: 128 },
  (_, i) => {
    const t = i / 127;
    const envelope = Math.sin(t * Math.PI) ** 0.45;
    const detail =
      0.35 + (Math.abs(Math.sin(i * 12.9898) * 43758.5453) % 1) * 0.65;
    return Number(Math.max(0.14, envelope * detail).toFixed(3));
  }
);

/** Duration in seconds that `mockPodcastPeaks` describes. */
export const mockPodcastDuration = 128;

/**
 * A lesson text written section by section (`## ` per plan section, after a
 * short intro), with the plan's sections as `GET /lessons/{id}/text` lists
 * them. The reader opens it as a contents list, one section at a time.
 */
export const mockSectionedLessonText = [
  'Plants make their own food from light, water and air.',
  '',
  '## Light reactions',
  '### Capturing light',
  'Chlorophyll absorbs light in the thylakoid membranes.',
  '',
  '## The Calvin cycle',
  '### Fixing carbon',
  'In the stroma, carbon dioxide is fixed into sugar.',
  '',
  '## Why it matters',
  '### Food webs',
  'Almost every food chain starts with photosynthesis.',
].join('\n');

export const mockLessonTextSections = [
  {
    index: 1,
    title: 'Light reactions',
    goal: 'See how light becomes chemical energy.',
    concepts: ['Capturing light'],
    ready: true,
  },
  {
    index: 2,
    title: 'The Calvin cycle',
    goal: 'Follow carbon into sugar.',
    concepts: ['Fixing carbon'],
    ready: true,
  },
  {
    index: 3,
    title: 'Why it matters',
    goal: 'Connect it to life on Earth.',
    concepts: ['Food webs'],
    ready: true,
  },
];

/**
 * A lesson's sections with their podcasts, as `GET /lessons/{id}/podcasts`
 * returns them for a Free account: the first section's podcast made, the
 * others not yet - and, on Free, only the first may have one.
 */
export const mockSectionPodcasts: SectionPodcastSummary[] = [
  {
    index: 1,
    title: 'Light reactions',
    concepts: ['Capturing light'],
    status: 'completed',
    duration: mockPodcastDuration,
    error: null,
    locked: false,
  },
  {
    index: 2,
    title: 'The Calvin cycle',
    concepts: ['Fixing carbon'],
    status: 'not_started',
    duration: 0,
    error: null,
    locked: true,
  },
  {
    index: 3,
    title: 'Why it matters',
    concepts: ['Food webs'],
    status: 'not_started',
    duration: 0,
    error: null,
    locked: true,
  },
];
